import { Player, type Player as PlayerType } from '@brightcove/web-sdk/ui';

import continueWatchingPlugin, {
  type ContinueWatchingPluginOptions,
} from '~/components/player/plugins/continue-watching';
import { isLive } from '~/components/player/utils';
import { toNumberWithDefaultZero } from '~/services/continue-watching/client';
import logger from '~/services/logger';

type ContinueWatchingHandler = (this: unknown, data?: unknown) => void;

type CreateContinueWatchingBridgeParams = {
  getCurrentTime: () => number;
  getDuration: () => number;
  getIsSeeking: () => boolean;
  onAdapterEventError: (event: string, error: unknown) => void;
};

type SetupContinueWatchingBridgeParams = {
  playerInstance: PlayerType | null;
  playerContainerRef: { current: HTMLDivElement | null };
  playableItem: any;
  pluginsData: any;
  isContinueWatchingSyncedFeed: boolean;
  continueWatchingUserIdentifier: string;
  continueWatchingRemoteEventsRequest: unknown;
  baseURL: string;
};

const createContinueWatchingBridge = ({
  getCurrentTime,
  getDuration,
  getIsSeeking,
  onAdapterEventError,
}: CreateContinueWatchingBridgeParams) => {
  const eventHandlers = new Map<string, Set<ContinueWatchingHandler>>();

  let playerAdapter: {
    currentTime: (time?: number | string) => number;
    duration: () => number;
    player: () => unknown;
    seeking: () => boolean;
    on: (event: string, handler: ContinueWatchingHandler) => void;
    off: (event: string, handler: ContinueWatchingHandler) => void;
    trigger: (event: string, data?: unknown) => void;
  };

  const emitAdapterEvent = (event: string, data?: unknown) => {
    const handlers = eventHandlers.get(event);

    if (!handlers) return;

    handlers.forEach((handler) => {
      try {
        handler.call(playerAdapter, data);
      } catch (error) {
        onAdapterEventError(event, error);
      }
    });
  };

  playerAdapter = {
    currentTime: (time?: number | string) => {
      if (time !== undefined) {
        return typeof time === 'string' ? parseFloat(time) : time;
      }

      return getCurrentTime();
    },
    duration: () => getDuration(),
    player: function () {
      return this;
    },
    seeking: () => getIsSeeking(),
    on: (event: string, handler: ContinueWatchingHandler) => {
      if (!eventHandlers.has(event)) {
        eventHandlers.set(event, new Set());
      }

      eventHandlers.get(event)!.add(handler);
    },
    off: (event: string, handler: ContinueWatchingHandler) => {
      const handlers = eventHandlers.get(event);

      if (!handlers) return;

      handlers.delete(handler);
    },
    trigger: (event: string, data?: unknown) => {
      emitAdapterEvent(event, data);
    },
  };

  return {
    playerAdapter,
    emitAdapterEvent,
    clearContinueWatchingBridge: () => {
      eventHandlers.clear();
    },
  };
};

/**
 * Wires the existing Video.js continue watching plugin into the Brightcove player
 * by providing a lightweight adapter and mapping Brightcove events to plugin events.
 *
 * This keeps plugin behavior consistent (resume/started/progress/stopped handling)
 * while preserving Brightcove-specific mute/icon reconciliation on resume playback.
 *
 * Returns a cleanup callback that removes all listeners and clears bridge handlers.
 */
export const setupContinueWatchingBridge = ({
  playerInstance,
  playerContainerRef,
  playableItem,
  pluginsData,
  isContinueWatchingSyncedFeed,
  continueWatchingUserIdentifier,
  continueWatchingRemoteEventsRequest,
  baseURL,
}: SetupContinueWatchingBridgeParams): (() => void) => {
  if (!playerInstance || !isContinueWatchingSyncedFeed) {
    return () => {};
  }

  let isSeekingState = false;
  let hasSeekedToResumeTime = false;
  let shouldPlayAfterSeek = false;
  let hasScheduledMuteReconcile = false;
  let isDisposed = false;
  const timeoutIds = new Set<ReturnType<typeof setTimeout>>();

  const { playerAdapter, emitAdapterEvent, clearContinueWatchingBridge } =
    createContinueWatchingBridge({
      getCurrentTime: () => playerInstance.getCurrentTime(),
      getDuration: () => playerInstance.getDuration(),
      getIsSeeking: () => isSeekingState,
      onAdapterEventError: (event, error) => {
        logger.error(
          `BrightcovePlayer: Error triggering adapter event ${event} - ${error}`
        );
      },
    });

  const scheduleTimeout = (callback: () => void, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    timeoutId = setTimeout(() => {
      timeoutIds.delete(timeoutId);

      if (isDisposed) {
        return;
      }

      callback();
    }, delay);

    timeoutIds.add(timeoutId);
  };

  const clearScheduledTimeouts = () => {
    timeoutIds.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    timeoutIds.clear();
  };

  const syncMuteIconWithUiToggle = (stage: string) => {
    if (isDisposed) {
      return;
    }

    try {
      const apiMuted = (playerInstance as any).getIsMuted?.();

      if (apiMuted === true && playerContainerRef.current) {
        const muteButton =
          playerContainerRef.current.querySelector<HTMLButtonElement>(
            '[aria-label*="mute" i], [class*="mute" i], [data-component*="mute" i]'
          );

        if (!muteButton) {
          return;
        }

        muteButton.click();
      }
    } catch (error) {
      logger.error(
        `BrightcovePlayer: Error syncing mute icon (${stage}) - ${error}`
      );
    }
  };

  const attemptUnmuteAndSync = (stage: string) => {
    if (isDisposed) {
      return;
    }

    try {
      const videoElement = (playerInstance as any).getVideoElement?.();
      (playerInstance as any).unmute();

      if (videoElement) {
        videoElement.muted = false;
        videoElement.defaultMuted = false;
      }

      syncMuteIconWithUiToggle(stage);
    } catch (error) {
      logger.error(
        `BrightcovePlayer: Error in unmute reconcile (${stage}) - ${error}`
      );
    }
  };

  const schedulePostPlayMuteReconcile = (stage: string) => {
    if (hasScheduledMuteReconcile) return;

    hasScheduledMuteReconcile = true;
    const delays = [10, 50, 150, 300, 600];

    delays.forEach((delay) => {
      scheduleTimeout(() => {
        attemptUnmuteAndSync(`${stage}-${delay}ms`);
      }, delay);
    });
  };

  const options: ContinueWatchingPluginOptions = {
    entryId: playableItem.id,
    resumeTime: playableItem.extensions?.resumeTime,
    entry: playableItem,
    isContinueWatchingSyncedFeed,
    continueWatchingUserIdentifier: continueWatchingUserIdentifier || '',
    continueWatchingRemoteEventsRequest:
      continueWatchingRemoteEventsRequest as any,
    continueWatchingPluginConfiguration:
      pluginsData?.continueWatching?.customConfiguration,
    getIsLive: () => isLive(playableItem),
    baseURL,
  };

  continueWatchingPlugin.call(playerAdapter as any, options);

  const onPlay = () => {
    emitAdapterEvent('play');
  };

  const onPause = () => {
    emitAdapterEvent('pause');
  };

  const onLoadedMetadata = () => {
    const currentResumeTime = toNumberWithDefaultZero(
      playableItem.extensions?.resumeTime
    );

    emitAdapterEvent('loadedmetadata');

    try {
      const currentMuted = (playerInstance as any).getIsMuted();

      if (currentResumeTime === 0 && currentMuted) {
        (playerInstance as any).unmute();
      }
    } catch (error) {
      logger.error(
        `BrightcovePlayer: Error applying persisted settings - ${error}`
      );
    }

    if (currentResumeTime > 0 && !hasSeekedToResumeTime) {
      hasSeekedToResumeTime = true;

      try {
        playerInstance.pause();

        const videoElement = (playerInstance as any).getVideoElement?.();

        if (videoElement && typeof videoElement.currentTime === 'number') {
          videoElement.currentTime = currentResumeTime;
          shouldPlayAfterSeek = true;
        } else {
          logger.error(
            'BrightcovePlayer: Could not access video element for seeking'
          );
          playerInstance.play();
        }
      } catch (error) {
        logger.error(
          `BrightcovePlayer: Error seeking to resume time - ${error}`
        );
        playerInstance.play();
      }
    }
  };

  const onTimeUpdate = () => {
    emitAdapterEvent('timeupdate');
  };

  const onEnded = () => {
    emitAdapterEvent('ended');
  };

  const onSeeking = () => {
    isSeekingState = true;
  };

  const onSeeked = () => {
    isSeekingState = false;

    if (shouldPlayAfterSeek) {
      shouldPlayAfterSeek = false;

      try {
        playerInstance.play();

        scheduleTimeout(() => {
          attemptUnmuteAndSync('seeked-10ms');
          schedulePostPlayMuteReconcile('seeked');
        }, 10);
      } catch (error) {
        logger.error(`BrightcovePlayer: Error playing after seek - ${error}`);
      }
    }
  };

  const onPlaying = () => {
    attemptUnmuteAndSync('playing-immediate');
    schedulePostPlayMuteReconcile('playing');
  };

  playerInstance.addEventListener(Player.Event.PlayerPlay, onPlay);
  playerInstance.addEventListener(Player.Event.PlayerPlaying, onPlaying);
  playerInstance.addEventListener(Player.Event.PlayerPause, onPause);
  playerInstance.addEventListener(
    Player.Event.PlayerLoadedMetadata,
    onLoadedMetadata
  );
  playerInstance.addEventListener(Player.Event.PlayerTimeChanged, onTimeUpdate);
  playerInstance.addEventListener(Player.Event.PlayerEnded, onEnded);
  playerInstance.addEventListener(Player.Event.PlayerSeeking, onSeeking);
  playerInstance.addEventListener(Player.Event.PlayerSeeked, onSeeked);

  return () => {
    if (isDisposed) {
      return;
    }

    isDisposed = true;
    clearScheduledTimeouts();

    playerAdapter.trigger('continue-watching:send-stopped-event');

    playerInstance.removeEventListener(Player.Event.PlayerPlay, onPlay);
    playerInstance.removeEventListener(Player.Event.PlayerPlaying, onPlaying);
    playerInstance.removeEventListener(Player.Event.PlayerPause, onPause);
    playerInstance.removeEventListener(
      Player.Event.PlayerLoadedMetadata,
      onLoadedMetadata
    );
    playerInstance.removeEventListener(
      Player.Event.PlayerTimeChanged,
      onTimeUpdate
    );
    playerInstance.removeEventListener(Player.Event.PlayerEnded, onEnded);
    playerInstance.removeEventListener(Player.Event.PlayerSeeking, onSeeking);
    playerInstance.removeEventListener(Player.Event.PlayerSeeked, onSeeked);
    clearContinueWatchingBridge();
  };
};
